/*
 * The cohort retention card's rule (ADR 0012 Decision 16), shared by the admin Overview's platform grid
 * (step 19) and the operator Overview's room grid (step 20): one reading of a stored grid, so the two
 * cards label rows and blank cells the same way. Declared structurally, as the rest of this package's
 * inputs are — the apps pass the API's DTO straight in.
 */

/** A stored cohort grid as the API reports it: activity months, and one row per cohort (null: joined before the grid). */
export interface CohortGrid {
  months: readonly string[];
  rows: readonly { cohort: string | null; cells: readonly ({ players: number; cohortSize: number } | null)[] }[];
}

export type CohortCard =
  | { kind: 'not-measured'; says: string }
  | {
      kind: 'measured';
      months: string[];
      rows: { label: string; cells: ({ players: number; size: number } | null)[] }[];
    };

/** A month with its year, from its first day, read in UTC. */
function monthAndYear(firstDay: string): string {
  const [y, m] = firstDay.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Cohort retention (Decision 16) — the platform's grid on the admin Overview, a room's on the operator
 * Overview: each cell how many of a cohort were active in a month over the cohort's size. Null until a month has been computed, which is said
 * without a number. Everybody first seen before the grid began is ONE row, named by the grid's first month —
 * the earliest cohort shown — and never split into months whose early cells nobody counted. A month before a
 * cohort joined has no cell: that is not nobody returning.
 */
export function cohortCard(cohort: CohortGrid | null): CohortCard {
  if (cohort === null) {
    return { kind: 'not-measured', says: 'Not measured yet — the first month is reported once every room has closed it.' };
  }
  const first = cohort.rows.find((r) => r.cohort !== null)?.cohort;
  return {
    kind: 'measured',
    months: cohort.months.map(monthAndYear),
    rows: cohort.rows.map((row) => ({
      label: row.cohort !== null
        ? `Joined ${monthAndYear(row.cohort)}`
        : first ? `Joined before ${monthAndYear(first)}` : 'Joined earlier',
      cells: row.cells.map((cell) => (cell ? { players: cell.players, size: cell.cohortSize } : null)),
    })),
  };
}
